import { Language } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import {
  alpha,
  Avatar,
  Box,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState, type ReactNode } from 'react';
import { Button } from '~/components/Button';
import { FaviconDomainInput } from '~/components/FaviconDomainInput';
import useRouter from '~/hooks/useRouter';
import { getHostname } from '~/scripts/parseDomain';

interface DomainNoResultsProps {
  domain: string;
}

export const DomainNoResults = ({ domain }: DomainNoResultsProps) => {
  const router = useRouter();
  const [searchInput, setSearchInput] = useState('');

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchInput.trim();
    if (!query) {
      return;
    }
    const hostname = getHostname(query);
    if (hostname) {
      router.push(`/domains/${hostname}`);
      return;
    }
    const search = { all: [query], any: [], none: [] };
    router.push(
      `/reports/new?search=${encodeURIComponent(JSON.stringify(search))}`
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 5 } }}>
      <Stack direction="row" spacing={3} alignItems="flex-start">
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: { xs: 'none', sm: 'flex' },
          }}>
          <SearchOffIcon fontSize="large" />
        </Avatar>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            We don&apos;t have results for <b>{domain}</b> yet
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This doesn&apos;t mean the site doesn&apos;t exist. It usually comes
            down to one of these two reasons:
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <ReasonCard
            icon={<ScheduleIcon color="primary" />}
            title="We haven't crawled it yet"
            description="We index 14.7M+ domains, but new, small or recently moved sites may not be in our crawl yet."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReasonCard
            icon={<BlockIcon color="primary" />}
            title="We couldn't access the page"
            description="The site may block crawlers, require a login, redirect to another domain, or have been down when we visited."
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="subtitle1" fontWeight={500} gutterBottom>
        Try a different search
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Another domain, like shopify.com, or a technology"
            slotProps={{
              input: {
                startAdornment: (
                  <FaviconDomainInput
                    value={searchInput}
                    fallback={<Language />}
                  />
                ),
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{ px: 4, ml: 0 }}>
            Search
          </Button>
        </Stack>
      </form>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          px: 3,
          py: 2.5,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between">
          <Typography variant="body1">
            Need data on <b>{domain}</b> specifically? We can crawl it for you.
          </Typography>
          <Button
            variant="outlined"
            href="/deepweb?utm_source=domain-no-results"
            endIcon={<ArrowForwardIcon />}
            sx={{ ml: 0, whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
            Request a custom crawl
          </Button>
        </Stack>
      </Paper>
    </Paper>
  );
};

interface ReasonCardProps {
  icon: ReactNode;
  title: string;
  description: string;
}

const ReasonCard = ({ icon, title, description }: ReasonCardProps) => {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, height: '100%', bgcolor: 'grey.50' }}>
      <Stack direction="row" spacing={2}>
        {icon}
        <Box>
          <Typography variant="subtitle1" fontWeight={500}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};
